/**
 * useDTMF - Hook para tocar tons DTMF usando Web Audio API
 * 
 * Replica exatamente as frequências dos tons DTMF padrão telefônico.
 * Cada dígito é uma combinação de duas frequências senoidais.
 */
import { useCallback, useRef, useEffect } from 'react';

type DTMFDigit = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '*' | '#';

// Frequências DTMF padrão ITU-T Q.23
// Cada dígito é composto por uma frequência de linha + coluna
const DTMF_FREQUENCIES: Record<DTMFDigit, [number, number]> = {
  '1': [697, 1209],
  '2': [697, 1336],
  '3': [697, 1477],
  '4': [770, 1209],
  '5': [770, 1336],
  '6': [770, 1477],
  '7': [852, 1209],
  '8': [852, 1336],
  '9': [852, 1477],
  '*': [941, 1209],
  '0': [941, 1336],
  '#': [941, 1477],
};

// Duração e volume do tom
const TONE_DURATION_MS = 120;
const TONE_VOLUME = 0.08; // Volume baixo para não ser intrusivo
const FADE_DURATION = 0.01; // 10ms de fade para evitar clicks

interface UseDTMFOptions {
  enabled?: boolean;
  volume?: number;
  duration?: number;
}

export function useDTMF(options: UseDTMFOptions = {}) {
  const { 
    enabled = true, 
    volume = TONE_VOLUME, 
    duration = TONE_DURATION_MS 
  } = options;
  
  const contextRef = useRef<AudioContext | null>(null);
  const activeOscillators = useRef<OscillatorNode[]>([]);
  
  // Inicializar AudioContext sob demanda (precisa de interação do usuário)
  const getContext = useCallback((): AudioContext | null => {
    if (!enabled) return null;
    
    if (!contextRef.current) {
      try {
        const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        if (AudioContextClass) {
          contextRef.current = new AudioContextClass();
        }
      } catch {
        console.warn('[useDTMF] Web Audio API não suportada');
        return null;
      }
    }
    
    // Retomar contexto se suspenso (política de autoplay)
    if (contextRef.current?.state === 'suspended') {
      contextRef.current.resume().catch(() => {});
    }
    
    return contextRef.current;
  }, [enabled]);
  
  // Tocar um dígito DTMF
  const play = useCallback((digit: string) => {
    const ctx = getContext();
    if (!ctx) return;
    
    const frequencies = DTMF_FREQUENCIES[digit as DTMFDigit];
    if (!frequencies) return;
    
    const [freqLow, freqHigh] = frequencies;
    const now = ctx.currentTime;
    const endTime = now + duration / 1000;
    
    try {
      // Criar osciladores para as duas frequências
      const osc1 = ctx.createOscillator();
      const osc2 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      const gain2 = ctx.createGain();
      
      osc1.type = 'sine';
      osc2.type = 'sine';
      osc1.frequency.setValueAtTime(freqLow, now);
      osc2.frequency.setValueAtTime(freqHigh, now);
      
      // Envelope ADSR simplificado (attack + release)
      gain1.gain.setValueAtTime(0, now);
      gain1.gain.linearRampToValueAtTime(volume, now + FADE_DURATION);
      gain1.gain.setValueAtTime(volume, endTime - FADE_DURATION);
      gain1.gain.linearRampToValueAtTime(0, endTime);
      
      gain2.gain.setValueAtTime(0, now);
      gain2.gain.linearRampToValueAtTime(volume, now + FADE_DURATION);
      gain2.gain.setValueAtTime(volume, endTime - FADE_DURATION);
      gain2.gain.linearRampToValueAtTime(0, endTime);
      
      // Conectar: osc -> gain -> destination
      osc1.connect(gain1);
      gain1.connect(ctx.destination);
      
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      
      // Iniciar e parar
      osc1.start(now);
      osc2.start(now);
      osc1.stop(endTime + 0.01);
      osc2.stop(endTime + 0.01);
      
      // Rastrear para cleanup
      activeOscillators.current.push(osc1, osc2);
      
      // Remover da lista após parar
      setTimeout(() => {
        activeOscillators.current = activeOscillators.current.filter(
          o => o !== osc1 && o !== osc2
        );
      }, duration + 50);
      
    } catch (error) {
      console.warn('[useDTMF] Erro ao tocar tom:', error);
    }
  }, [getContext, duration, volume]);
  
  // Parar todos os tons ativos
  const stopAll = useCallback(() => {
    activeOscillators.current.forEach(osc => {
      try {
        osc.stop();
      } catch {
        // Já parado
      }
    });
    activeOscillators.current = [];
  }, []);
  
  // Cleanup ao desmontar
  useEffect(() => {
    return () => {
      stopAll();
      if (contextRef.current) {
        contextRef.current.close().catch(() => {});
        contextRef.current = null;
      }
    };
  }, [stopAll]);
  
  return {
    play,
    stopAll,
    isSupported: typeof window !== 'undefined' && 
      !!(window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext),
  };
}
